import type { ReactNode } from 'react';
import { cn } from './cn.ts';

export function PageHeader({
  title,
  description,
  actions,
  breadcrumb,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  breadcrumb?: ReactNode;
}) {
  return (
    <header className="flex flex-col gap-4 border-b border-secondary pb-5 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        {breadcrumb ? <div className="mb-1.5 text-sm text-tertiary">{breadcrumb}</div> : null}
        <h1 className="text-display-xs font-semibold text-primary">{title}</h1>
        {description ? <p className="mt-1 max-w-3xl text-sm text-tertiary">{description}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </header>
  );
}

export function Card({
  children,
  className,
  padded = true,
}: {
  children: ReactNode;
  className?: string;
  padded?: boolean;
}) {
  return (
    <section
      className={cn(
        'rounded-lg border border-secondary bg-primary shadow-xs',
        padded && 'p-5',
        className,
      )}
    >
      {children}
    </section>
  );
}

export function CardHeader({
  title,
  description,
  actions,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 border-b border-secondary px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <h2 className="text-md font-semibold text-primary">{title}</h2>
        {description ? <p className="mt-0.5 text-sm text-tertiary">{description}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
}

/** Label/value pairs — the shape most of this shell's detail panes are. */
export function DefinitionList({ children }: { children: ReactNode }) {
  return <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-[10rem_1fr]">{children}</dl>;
}

export function Definition({ term, children }: { term: ReactNode; children: ReactNode }) {
  return (
    <>
      <dt className="text-sm text-tertiary">{term}</dt>
      <dd className="text-sm text-primary">{children}</dd>
    </>
  );
}

export function Stack({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('flex flex-col gap-6', className)}>{children}</div>;
}

/** A uuid, rendered so it can be read and copied without dominating the row. */
export function Mono({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <code className={cn('font-mono text-xs break-all text-quaternary', className)}>{children}</code>
  );
}
